import { useContext, useMemo } from "react";
import AuthContext from "../../Context/AuthContext";

/**
 * The organisation identity used on every report, statement and printout.
 *
 * Values come from the `acc_config` rows the backend mirrors from the company
 * profile (see Company::__sync_to_config), so editing the company profile or
 * the Organisation section of System Config updates every document header.
 */

/** Initials used as the badge when the organisation has not uploaded one.
 *  Documents carry the organisation's identity, never the system's crest. */
export const orgInitials = (name = "") =>
  String(name)
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((w) => w[0].toUpperCase())
    .join("") || "—";

/** Turn a possibly-relative logo path into something a new window can load. */
export const absoluteUrl = (url) => {
  if (!url) return "";
  if (/^(https?:|data:)/i.test(url)) return url;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return origin + (url.startsWith("/") ? url : `/${url}`);
};

export const INDUSTRY_LABELS = {
  school: "Education / School",
  services: "Services",
  trading: "Trading / Retail",
  manufacturing: "Manufacturing",
  ngo: "NGO / Non-profit",
  hospitality: "Hospitality",
  healthcare: "Healthcare / Clinic",
  generic: "Generic / Other",
};

/**
 * Build the profile from a flat acc_config map. Pure, so it is easy to test.
 *
 * `fallback` is the company whose books are open — its name and currency stand
 * in until the Organisation section of System Config has been filled in, so a
 * fresh company's documents carry its own name rather than a placeholder, and
 * never the previous company's.
 */
export const buildOrgProfile = (config = {}, industry = "generic", fallback = {}) => {
  const cfg = config || {};
  const pick = (...keys) => {
    for (const k of keys) {
      const v = cfg[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim();
    }
    return "";
  };
  const orgName =
    pick("org_name", "company_name") ||
    (fallback?.name ? String(fallback.name).trim() : "") ||
    "Your Organisation";
  return {
    name: orgName,
    address: pick("org_address"),
    phone: pick("org_phone"),
    email: pick("org_email"),
    website: pick("org_website"),
    tin: pick("org_tin"),
    currency: pick("org_currency") || fallback?.currency || "UGX",
    // Only the organisation's own badge. A report belongs to the school or
    // company that issued it, so the Accountify crest must never stand in for
    // it — without a badge we fall back to the organisation's initials.
    logo: absoluteUrl(pick("org_logo", "org_logo_url")),
    hasOwnLogo: !!pick("org_logo", "org_logo_url"),
    initials: orgInitials(orgName),
    footerNote: pick("invoice_footer"),
    industry,
    industryLabel: INDUSTRY_LABELS[industry] || INDUSTRY_LABELS.generic,
  };
};

/** Contact line shown under the organisation name on documents. */
export const orgContactLine = (org) =>
  [org.address, org.phone && `Tel: ${org.phone}`, org.email, org.website]
    .filter(Boolean)
    .join("  •  ");

const useOrgProfile = () => {
  const { companyConfig, industry, company } = useContext(AuthContext) || {};
  return useMemo(
    () => buildOrgProfile(companyConfig, industry || "generic", company || {}),
    [companyConfig, industry, company]
  );
};

export default useOrgProfile;
